import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Pencil, Trash2, MapPin } from "lucide-react";
import { adminBookings, adminUpdateBooking, adminDeleteBooking, apiError } from "../../lib/api";
import { Field, Input, Textarea, Select, Btn, Modal, StatusPill } from "./AdminKit";

const STATUS = ["booked", "out", "returned"];
const dot = { booked: "bg-sky-500", out: "bg-nead-pink", returned: "bg-emerald-500" };

function ymd(d) { return d.toISOString().slice(0, 10); }

export default function AdminBookings() {
  const [list, setList] = useState([]);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => adminBookings().then(setList).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const quickStatus = async (b, status) => {
    try { await adminUpdateBooking(b.id, { status }); toast.success(`Marked ${status}.`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (b) => {
    if (!window.confirm("Delete this booking?")) return;
    try { await adminDeleteBooking(b.id); toast.success("Deleted."); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const openEdit = (b) => { setEditing(b); setForm({ location: b.location || "", notes: b.notes || "", date_from: b.date_from || "", date_to: b.date_to || "", status: b.status }); };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await adminUpdateBooking(editing.id, form); toast.success("Booking updated."); setEditing(null); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  // calendar grid
  const days = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const start = new Date(first);
    start.setDate(first.getDate() - ((first.getDay() + 6) % 7)); // Monday start
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [month]);

  const bookingsOnDay = (d) => {
    const s = ymd(d);
    return list.filter((b) => b.date_from && b.date_to && b.date_from <= s && s <= b.date_to && b.status !== "returned");
  };

  return (
    <div>
      <h1 className="font-display font-bold text-2xl text-nead-navy">Bookings</h1>
      <p className="mt-1 text-sm text-nead-navy/55">See what's booked out, where it is, and mark items back in when they return.</p>

      {/* Calendar */}
      <div className="mt-8 rounded-2xl border border-black/[0.07] bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-lg text-nead-navy">{month.toLocaleString("en-GB", { month: "long", year: "numeric" })}</h2>
          <div className="flex gap-2">
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="cal-prev" className="rounded-lg border border-black/10 p-2 hover:border-nead-pink"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="cal-next" className="rounded-lg border border-black/10 p-2 hover:border-nead-pink"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase tracking-wider text-nead-navy/40 mb-1">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d, i) => {
            const inMonth = d.getMonth() === month.getMonth();
            const bs = bookingsOnDay(d);
            return (
              <div key={i} className={`min-h-[64px] rounded-lg border p-1.5 text-left ${inMonth ? "border-black/[0.06] bg-white" : "border-transparent bg-nead-mist/40 text-nead-navy/30"}`}>
                <div className="text-xs font-medium text-nead-navy/60">{d.getDate()}</div>
                <div className="mt-1 space-y-0.5">
                  {bs.slice(0, 2).map((b) => (
                    <div key={b.id} className="flex items-center gap-1 truncate text-[10px] text-nead-navy/70">
                      <span className={`h-1.5 w-1.5 flex-none rounded-full ${dot[b.status]}`} />{b.customer_name}
                    </div>
                  ))}
                  {bs.length > 2 && <div className="text-[10px] text-nead-navy/40">+{bs.length - 2} more</div>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex gap-5 text-xs text-nead-navy/55">
          {STATUS.map((s) => <span key={s} className="flex items-center gap-1.5 capitalize"><span className={`h-2 w-2 rounded-full ${dot[s]}`} />{s}</span>)}
        </div>
      </div>

      {/* List */}
      <div className="mt-8 space-y-4">
        {list.map((b) => (
          <div key={b.id} data-testid={`booking-${b.id}`} className="rounded-2xl border border-black/[0.07] bg-white p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="font-display font-bold text-lg text-nead-navy">{b.customer_name}</h3>
                  <StatusPill status={b.status} />
                </div>
                <div className="mt-1 text-sm text-nead-navy/60">{b.date_from || "?"} to {b.date_to || "?"}</div>
                <div className="mt-2 text-sm text-nead-navy/70">{(b.items || []).map((i) => `${i.quantity}× ${i.name}`).join(", ")}</div>
                {b.location && <div className="mt-2 flex items-center gap-1.5 text-sm text-nead-navy/60"><MapPin className="h-3.5 w-3.5" />{b.location}</div>}
                {b.notes && <p className="mt-2 text-sm text-nead-navy/60 bg-nead-mist rounded-lg p-3">{b.notes}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                {STATUS.map((s) => (
                  <Btn key={s} variant={b.status === s ? "dark" : "outline"} onClick={() => quickStatus(b, s)} data-testid={`booking-${b.id}-${s}`} className="capitalize">{s === "out" ? "Booked out" : s === "returned" ? "Returned" : "Booked"}</Btn>
                ))}
                <Btn variant="ghost" onClick={() => openEdit(b)} data-testid={`booking-edit-${b.id}`}><Pencil className="h-4 w-4" /></Btn>
                <Btn variant="ghost" onClick={() => remove(b)} data-testid={`booking-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Btn>
              </div>
            </div>
          </div>
        ))}
        {!list.length && <div className="rounded-2xl border border-black/[0.07] bg-white p-10 text-center text-nead-navy/40">No bookings yet. Approve a hire enquiry to create one.</div>}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit booking">
        <form onSubmit={save} className="space-y-4" data-testid="booking-form">
          <Field label="Status">
            <Select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} data-testid="booking-status">
              {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="From"><Input type="date" value={form.date_from} onChange={(e) => setForm((f) => ({ ...f, date_from: e.target.value }))} /></Field>
            <Field label="To"><Input type="date" value={form.date_to} onChange={(e) => setForm((f) => ({ ...f, date_to: e.target.value }))} /></Field>
          </div>
          <Field label="Location / where it is"><Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} data-testid="booking-location" placeholder="e.g. Whitehaven Harbour" /></Field>
          <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="booking-notes" placeholder="Condition on return, damage, etc." /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <Btn type="button" variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="booking-save">{busy ? "Saving…" : "Save"}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
